// src/pages/app/DashboardPage.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  Divider,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RepeatIcon from "@mui/icons-material/Repeat";
import InsightsIcon from "@mui/icons-material/Insights";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useNavigate } from "react-router-dom";

import PageHeader from "../../components/common/PageHeader";
import { useListTransfersQuery } from "../../features/transfers/transfersApi";
import type { TransferStatus } from "../../features/transfers/types";

const STATUS_LABELS: Record<string, string> = {
  planned: "Planned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};
function statusLabel(s: string) {
  return STATUS_LABELS[s] ?? s;
}

// --- helpers ---
const pad2 = (n: number) => String(n).padStart(2, "0");
const toYmd = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDays = (d: Date, days: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

type StatCardProps = {
  label: string;
  value: string;
  hint?: string;
};

function StatCard({ label, value, hint }: StatCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        height: "100%",
        borderTop: "3px solid",
        borderTopColor: "primary.main",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function DashboardPage() {
  const nav = useNavigate();

  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => toYmd(today), [today]);
  const weekToStr = useMemo(() => toYmd(addDays(today, 7)), [today]);

  const [statusFilter, setStatusFilter] = useState<TransferStatus | "">("");

  // ✅ Today list
  const {
    data: todayData,
    isLoading: isLoadingToday,
    isFetching: isFetchingToday,
    error: todayError,
  } = useListTransfersQuery({
    date: todayStr, // backend supports ?date=
    status: statusFilter || undefined,
    ordering: "service_date",
    page: 1,
  });

  // ✅ Week list for stats (no status filter εδώ, για να έχεις overall)
  const {
    data: weekData,
    isLoading: isLoadingWeek,
    isFetching: isFetchingWeek,
    error: weekError,
  } = useListTransfersQuery({
    date_from: todayStr,
    date_to: weekToStr,
    ordering: "service_date",
    page: 1,
  });

  const todayRows = useMemo(() => todayData?.results ?? [], [todayData?.results]);
  const weekRows = useMemo(() => weekData?.results ?? [], [weekData?.results]);

  // --- Stats (frontend computed) ---
  const todayCount = todayData?.count ?? todayRows.length;
  const weekCount = weekData?.count ?? weekRows.length;

  const unassignedCount = useMemo(() => {
    // provider is null in your Transfer model
    return weekRows.filter((t: any) => !t.provider).length;
  }, [weekRows]);

  const billingReadyCount = useMemo(() => {
    // MVP: completed within week range (ή μπορείς να το κάνεις month)
    return weekRows.filter((t: any) => t.status === "completed").length;
  }, [weekRows]);

  const loading = isLoadingToday || isLoadingWeek;
  const fetching = isFetchingToday || isFetchingWeek;

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={`Overview for ${todayStr}`}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav("/app/transfers/new")} size="medium">
              New Transfer
            </Button>
            <Button variant="outlined" startIcon={<RepeatIcon />} onClick={() => nav("/app/series/new")} size="medium">
              New Series
            </Button>
            <Button variant="outlined" startIcon={<InsightsIcon />} onClick={() => nav("/app/analytics")} size="medium">
              Analytics
            </Button>
          </Stack>
        }
      />

      {/* Top stats */}
      <Grid container spacing={2}>
<Grid size={{ xs: 12, md: 3 }}>
          <StatCard label="Today" value={loading ? "—" : String(todayCount)} hint="Transfers scheduled today" />
        </Grid>
<Grid size={{ xs: 12, md: 3 }}>
          <StatCard label="This week" value={loading ? "—" : String(weekCount)} hint="Next 7 days" />
        </Grid>
<Grid size={{ xs: 12, md: 3 }}>
          <StatCard label="Unassigned" value={loading ? "—" : String(unassignedCount)} hint="No provider selected" />
        </Grid>
<Grid size={{ xs: 12, md: 3 }}>
          <StatCard label="Billing ready" value={loading ? "—" : String(billingReadyCount)} hint="Completed in range" />
        </Grid>

        {/* Today schedule */}
        <Grid size={{ xs: 12 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              borderTop: "3px solid",
              borderTopColor: "primary.main",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SyncAltIcon fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Today schedule
                </Typography>
                {fetching ? <CircularProgress size={18} /> : null}
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                <Chip
                  clickable
                  label="All"
                  size="small"
                  variant={statusFilter === "" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("")}
                />
                <Chip
                  clickable
                  label="Planned"
                  size="small"
                  variant={statusFilter === "planned" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("planned")}
                />
                <Chip
                  clickable
                  label="In progress"
                  size="small"
                  variant={statusFilter === "in_progress" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("in_progress")}
                />
                <Chip
                  clickable
                  label="Completed"
                  size="small"
                  variant={statusFilter === "completed" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("completed")}
                />
                <Chip
                  clickable
                  label="Cancelled"
                  size="small"
                  variant={statusFilter === "cancelled" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("cancelled")}
                />
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {(todayError || weekError) ? (
              <Typography color="error">Failed to load dashboard data.</Typography>
            ) : isLoadingToday ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : todayRows.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  No transfers for today
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Use <strong>New Transfer</strong> above to create your first transfer.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {todayRows.map((t: any) => {
                  const primary = t.start_time ? `${t.start_time} • ${t.description || "Transfer"}` : (t.description || "Transfer");
                  const secondary = [
                    t.client ? `Client: ${t.client_name ?? t.client}` : null,
                    t.provider ? `Provider: ${t.provider_name ?? t.provider}` : "Provider: —",
                    `Status: ${t.status}`,
                  ].filter(Boolean).join(" • ");

                  return (
                    <ListItemButton
                      key={t.id}
                      onClick={() => nav(`/app/transfers/${t.id}/edit`)}
                      sx={{
                        borderRadius: 2,
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 1,
                      }}
                    >
                      <ListItemText primary={primary} secondary={secondary} sx={{ minWidth: 0 }} />
                      <Chip size="small" label={statusLabel(t.status)} sx={{ flexShrink: 0 }} />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
  